import type { EntityType } from '@repo/common/enums/entityType';
import { findIndex, includes, size } from 'lodash-es';
import type { INotification } from '../../../api/types/models/notification';
import { ENTITY_TYPES_MODAL_CONFIG } from '../../../components/EntityModal/constants';
import type {
	EnhancedNotification,
	EventType,
	NotificationPage,
	NotificationQueryData,
} from '../types';

export function updateNotificationReadStatus(
	old: NotificationQueryData | undefined,
	notificationId: string,
	read: boolean
): NotificationQueryData | undefined {
	if (!old?.pages) return old;

	return {
		...old,
		pages: old.pages.map((pageRes: NotificationPage) => ({
			...pageRes,
			results: pageRes.results.map((notification: INotification) =>
				notification.id === notificationId
					? { ...notification, read }
					: notification
			),
		})),
	};
}

export const moveNotification = (
	notifications: INotification[] | EnhancedNotification[],
	id: string,
	direction: 'up' | 'down'
) => {
	const index = findIndex(notifications, { id });

	if (
		index === -1 ||
		(direction === 'up' && index <= 0) ||
		(direction === 'down' && index === size(notifications) - 1)
	) {
		return null;
	}

	return direction === 'up'
		? notifications[index - 1]
		: notifications[index + 1];
};

export function getNotificationType(
	eventTypes: EventType[],
	event: string
): 'notifications' | 'tasks' {
	const type: 'notifications' | 'tasks' = 'notifications';

	const foundEventType = eventTypes.find((task) =>
		includes(task.values, event)
	);

	if (foundEventType && foundEventType.isTask) {
		return 'tasks';
	}

	return type;
}

/**
 * Extracts the changed field from a change request changes object.
 * Looks for either 'description' or 'definition' fields.
 *
 * @param changes - The changes object from a change request
 * @returns The name of the changed field ('description' or 'definition') or undefined if neither is found
 */
export function getChangedField(
	changes: Record<string, any>
): 'description' | 'definition' | undefined {
	return Object.keys(changes).find(
		(key) => key === 'description' || key === 'definition'
	) as 'description' | 'definition' | undefined;
}

/**
 * Determines which field (description or definition) should be updated based on entity type configuration.
 *
 * @param entity_type - The entity type to check
 * @returns The field that should be updated ('description' or 'definition')
 */
export function getFieldToUpdate(
	entity_type: EntityType
): 'description' | 'definition' {
	const config = ENTITY_TYPES_MODAL_CONFIG[entity_type];
	return config?.richTextTemplateField || 'description';
}
